import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, AlertTriangle, Link2, Check, X } from 'lucide-react';
import { RubyLoader } from '../../../components/RubyLoader';
import { toastService } from '../../../services/toastService';
import { migrationApi, type MigrationItem, type MigrationJob } from '../../../services/migrationApi';
import { Btn, Card, Chips, StatTile, Switch, Banner, EDITABLE, fmtMoney, toastApiError } from './shared';

type ItemFilter = 'all' | 'issues' | 'skipped' | 'existing' | 'create';

const APPLY_PILL: Record<string, string> = {
  pending: 'bg-stone-100 text-stone-600',
  created: 'bg-sky-50 text-sky-700',
  draft_only: 'bg-amber-50 text-amber-800',
  published: 'bg-green-50 text-green-700',
  stocked: 'bg-green-50 text-green-700',
  failed: 'bg-rose-50 text-rose-700',
  rolled_back: 'bg-stone-200 text-stone-700',
};

const matches = (it: MigrationItem, f: ItemFilter) =>
  f === 'all' ? true
    : f === 'issues' ? it.issues.length > 0
      : f === 'skipped' ? it.decision === 'skip'
        : f === 'existing' ? it.match?.kind === 'existing'
          : it.decision === 'create';

const ItemRow: React.FC<{
  item: MigrationItem;
  editable: boolean;
  locName: (id: string) => string;
  onSaved: (item: MigrationItem) => void;
  job: MigrationJob;
}> = ({ item, editable, locName, onSaved, job }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: item.name, category: item.category || '', brand: item.brand || '' });
  const [busy, setBusy] = useState<'save' | 'decision' | null>(null);

  const patch = async (p: Parameters<typeof migrationApi.patchItem>[3], kind: 'save' | 'decision') => {
    setBusy(kind);
    try {
      const r = await migrationApi.patchItem(job.tenantId, job.jobId, item.itemKey, p);
      onSaved(r.item);
      if (kind === 'save') { setEditing(false); toastService.success('Item updated'); }
    } catch (e) {
      toastApiError(e, 'Could not update the item');
    } finally {
      setBusy(null);
    }
  };

  const save = () => patch({
    name: form.name.trim() || item.name,
    // '' (not undefined) so clearing a field reaches the server — JSON drops undefined keys.
    category: form.category.trim(),
    brand: form.brand.trim(),
  }, 'save');

  const stockLocs = useMemo(() => {
    const s = new Set<string>();
    item.variants.forEach((v) => Object.keys(v.stock || {}).forEach((k) => s.add(k)));
    return [...s];
  }, [item.variants]);
  const include = item.decision === 'create';
  const input = 'h-11 rounded-lg border border-stone-300 px-3 text-sm outline-none focus:border-[#DC143C] focus:ring-2 focus:ring-[#DC143C]/15 min-w-0';

  return (
    <div className={`border-b border-stone-100 last:border-b-0 ${include ? '' : 'bg-stone-50/70'}`}>
      <div className="px-4 py-2 flex items-start gap-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="h-11 w-11 -ml-2 grid place-items-center text-stone-400 hover:text-stone-700 shrink-0" aria-label={open ? 'Hide variants' : 'Show variants'} aria-expanded={open}>
          {open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <div className="flex-1 min-w-0 py-1.5">
          {editing ? (
            <div className="grid sm:grid-cols-3 gap-2">
              <input className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Name" placeholder="Name" autoFocus />
              <input className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} aria-label="Category" placeholder="Category" />
              <input className={input} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} aria-label="Brand" placeholder="Brand" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-medium ${include ? 'text-stone-900' : 'text-stone-400 line-through'}`}>{item.name || '(no name)'}</span>
                <span className="text-xs text-stone-400">{item.variants.length} variant{item.variants.length === 1 ? '' : 's'}</span>
                {item.match?.kind === 'existing' && (
                  <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 inline-flex items-center gap-1">
                    <Link2 size={11} />EXISTS{item.match.by ? ` · by ${item.match.by}` : ''}
                  </span>
                )}
                {item.edited && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">EDITED</span>}
                {item.apply?.status && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${APPLY_PILL[item.apply.status] || 'bg-stone-100'}`}>{item.apply.status.replace(/_/g, ' ').toUpperCase()}</span>}
              </div>
              <div className="text-xs text-stone-500 mt-0.5 truncate">
                {[item.category && `Category: ${item.category}`, item.brand && `Brand: ${item.brand}`, item.vendor && `Vendor: ${item.vendor}`].filter(Boolean).join(' · ') || 'No category or brand'}
              </div>
            </>
          )}
          {item.issues.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {item.issues.map((iss, i) => (
                <span key={i} title={iss.code} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 inline-flex items-center gap-1">
                  <AlertTriangle size={11} />{iss.message}
                </span>
              ))}
            </div>
          )}
          {!!item.publishBlockers?.length && include && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-1.5 inline-block">
              Will be created as a draft: {item.publishBlockers.join(', ')}
            </div>
          )}
          {item.apply?.error && <div className="text-xs text-rose-700 mt-1">{item.apply.error}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editing ? (
            <>
              <Btn kind="black" busy={busy === 'save'} onClick={save} icon={<Check size={16} />} aria-label="Save item">Save</Btn>
              <button type="button" onClick={() => { setEditing(false); setForm({ name: item.name, category: item.category || '', brand: item.brand || '' }); }} className="h-11 w-11 grid place-items-center text-stone-400 hover:text-stone-700" aria-label="Cancel edit"><X size={18} /></button>
            </>
          ) : (
            <button type="button" disabled={!editable} onClick={() => setEditing(true)} className="h-11 w-11 grid place-items-center text-stone-400 hover:text-stone-700 disabled:opacity-30" aria-label={`Edit ${item.name}`}>
              <Pencil size={16} />
            </button>
          )}
          <Switch
            on={include}
            busy={busy === 'decision'}
            disabled={!editable || editing}
            label={include ? `Exclude ${item.name}` : `Include ${item.name}`}
            onClick={() => patch({ decision: include ? 'skip' : 'create' }, 'decision')}
          />
        </div>
      </div>
      {open && (
        <div className="px-4 pb-3 pl-12 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-stone-500 border-b border-stone-200">
                <th className="py-1.5 pr-3 font-semibold">Variant</th>
                <th className="py-1.5 pr-3 font-semibold">SKU</th>
                <th className="py-1.5 pr-3 font-semibold">Barcodes</th>
                <th className="py-1.5 pr-3 font-semibold text-right">Price</th>
                <th className="py-1.5 pr-3 font-semibold text-right">Cost</th>
                {stockLocs.map((l) => <th key={l} className="py-1.5 pr-3 font-semibold text-right whitespace-nowrap">Stock · {locName(l)}</th>)}
                <th className="py-1.5 font-semibold text-right">Row</th>
              </tr>
            </thead>
            <tbody>
              {item.variants.map((v, i) => (
                <tr key={i} className="border-b border-stone-100 last:border-b-0 text-stone-700">
                  <td className="py-1.5 pr-3">{v.name || (v.options ? Object.values(v.options).join(' / ') : '') || 'Default'}</td>
                  <td className="py-1.5 pr-3 font-mono">{v.sku || '—'}</td>
                  <td className="py-1.5 pr-3 font-mono">{v.barcodes?.length ? v.barcodes.join(', ') : '—'}</td>
                  <td className="py-1.5 pr-3 text-right" title={v.prices ? Object.entries(v.prices).map(([k, p]) => `${locName(k)}: ${fmtMoney(p)}`).join('\n') : undefined}>
                    {fmtMoney(v.price)}{v.prices && Object.keys(v.prices).length ? <span className="text-stone-400"> +{Object.keys(v.prices).length} loc</span> : null}
                  </td>
                  <td className="py-1.5 pr-3 text-right">{fmtMoney(v.cost)}</td>
                  {stockLocs.map((l) => <td key={l} className="py-1.5 pr-3 text-right">{v.stock?.[l] ?? '—'}</td>)}
                  <td className="py-1.5 text-right text-stone-400">{v.sourceRow ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {item.description && <p className="text-xs text-stone-500 mt-2 line-clamp-3">{item.description}</p>}
        </div>
      )}
    </div>
  );
};

export const ReviewStep: React.FC<{
  job: MigrationJob;
  locations: { locationId: string; name: string }[];
  reload: () => void;
}> = ({ job, locations, reload }) => {
  const [items, setItems] = useState<MigrationItem[] | null>(null);
  const [next, setNext] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ItemFilter>('all');
  const editable = EDITABLE.includes(job.status);
  const locName = useCallback((id: string) => locations.find((l) => l.locationId === id)?.name || id, [locations]);

  const loadFirst = useCallback(async () => {
    setError(null);
    try {
      const r = await migrationApi.items(job.tenantId, job.jobId);
      setItems(r.items || []);
      setNext(r.next);
    } catch (e: any) {
      setItems([]);
      setError(e?.message || 'Could not load items');
    }
  }, [job.tenantId, job.jobId]);

  // Reload when a new analysis lands (the stats change) or the apply moves on.
  const sig = `${job.status}|${job.stats?.items}|${job.stats?.rows}`;
  useEffect(() => { loadFirst(); }, [loadFirst, sig]);

  const more = async () => {
    if (!next) return;
    setLoadingMore(true);
    try {
      const r = await migrationApi.items(job.tenantId, job.jobId, next);
      setItems((prev) => [...(prev || []), ...(r.items || [])]);
      setNext(r.next);
    } catch (e) {
      toastApiError(e, 'Could not load more items');
    } finally {
      setLoadingMore(false);
    }
  };

  const onSaved = (it: MigrationItem) => {
    setItems((prev) => (prev || []).map((x) => (x.itemKey === it.itemKey ? it : x)));
    // An edit after a dry run sends the job back to 'mapped' server-side.
    if (job.status === 'dry_run_ready') reload();
  };

  const list = items || [];
  const counts = {
    all: list.length,
    issues: list.filter((i) => matches(i, 'issues')).length,
    skipped: list.filter((i) => matches(i, 'skipped')).length,
    existing: list.filter((i) => matches(i, 'existing')).length,
    create: list.filter((i) => matches(i, 'create')).length,
  };
  const shown = list.filter((i) => matches(i, filter));
  const s = job.stats || {};

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatTile label="Rows" value={s.rows} />
        <StatTile label="Products" value={s.items} />
        <StatTile label="Variants" value={s.variants} />
        <StatTile label="Will create" value={s.toCreate} tone="ruby" />
        <StatTile label="Skip" value={s.toSkip} />
        <StatTile label="With issues" value={s.withIssues} tone={s.withIssues ? 'amber' : 'plain'} />
        <StatTile label="Barcodes" value={s.barcodes} />
        <StatTile label="New categories" value={s.categoriesNew} />
        <StatTile label="New brands" value={s.brandsNew} />
        <StatTile label="Stock rows" value={s.stockRows} />
      </div>

      {!editable && items !== null && <Banner tone="sky">Items are read-only while the job is {job.status.replace(/_/g, ' ')}.</Banner>}

      <Card
        title="Items"
        actions={<span className="text-xs text-stone-400">{list.length} loaded{next ? ' · more available' : ''}</span>}
      >
        <div className="mb-3">
          <Chips
            value={filter}
            onChange={(v) => setFilter(v as ItemFilter)}
            options={[
              { id: 'all', label: 'All', count: counts.all },
              { id: 'issues', label: 'With issues', count: counts.issues },
              { id: 'skipped', label: 'Skipped', count: counts.skipped },
              { id: 'existing', label: 'Existing match', count: counts.existing },
              { id: 'create', label: 'Will create', count: counts.create },
            ]}
          />
          {next && filter !== 'all' && <p className="text-xs text-stone-400 mt-2">Filters apply to the loaded items — load more to search the rest.</p>}
        </div>
        {error && <Banner tone="rose" icon={<AlertTriangle size={16} />}>{error}</Banner>}
        {items === null ? (
          <RubyLoader variant="section" label="Loading items" />
        ) : shown.length === 0 ? (
          <div className="text-sm text-stone-400 py-6 text-center">{list.length ? 'No items match this filter.' : 'No items yet — analyse the files first.'}</div>
        ) : (
          <div className="-mx-5 border-t border-stone-100">
            {shown.map((it) => (
              <ItemRow key={it.itemKey} item={it} editable={editable} locName={locName} onSaved={onSaved} job={job} />
            ))}
          </div>
        )}
        {next && (
          <div className="pt-4 flex justify-center">
            <Btn busy={loadingMore} onClick={more}>Load more</Btn>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReviewStep;
